import { snakeToCamel } from '@mint-lib/api';
import type { Chat } from '@mint-sdk/api-ai-assistant';

import type { PrimitiveDataframeRow } from '../types/charts.js';

type Props = {
  dataframe: Chat.DataframeType;
  graph: Chat.Graph;
};

const useStructuredDataframe = ({ dataframe, graph }: Props) => {
  if (!dataframe.length) {
    return { chartDataframe: [], tableDataframe: [], hasWrongData: true };
  }

  const tableDataframe = removeBooleanValuesFromDataframe(dataframe); // Charts don't support boolean values
  const { chartDataframe, hasWrongData } = extractDataFromDataframe(
    tableDataframe,
    graph.instructions,
  ); // FYI: A dataframe can contain more columns than are required for a chart

  return { chartDataframe, tableDataframe, hasWrongData };
};

export { useStructuredDataframe };

export const extractDataFromDataframe = (
  dataframe: Record<string, PrimitiveDataframeRow>[],
  instructions: Chat.Graph['instructions'],
) => {
  const { x, y, label } = instructions;

  const formattedX = snakeToCamel(x);
  const formattedYs = y.map(snakeToCamel);
  const formattedLabel = snakeToCamel(label ?? '');
  const formattedDataframe = dataframe.map((item) => {
    const newItem = { ...item };

    for (const y of formattedYs) {
      if (newItem[y] === null) {
        newItem[y] = 0;
      }
    }
    return newItem;
  });

  const instructionsData = [formattedX, ...formattedYs];

  const hasWrongData =
    formattedDataframe.some((item) => {
      return instructionsData.some((key) => !Object.keys(item).includes(key));
    }) ?? true;

  const chartDataframe = formattedDataframe.map((row) =>
    pickColumns(row, [formattedX, ...formattedYs, formattedLabel]),
  );
  return { chartDataframe, hasWrongData };
};

export const removeBooleanValuesFromDataframe = (
  dataframe?: Chat.DataframeType,
) =>
  (dataframe ?? []).map((row) => {
    const entries = Object.entries(row).filter(
      ([_, value]) => typeof value !== 'boolean',
    );

    return Object.fromEntries(entries) as Record<string, PrimitiveDataframeRow>;
  });

const pickColumns = <T, K extends keyof T>(
  object: T,
  keys: readonly K[],
): Pick<T, K> => {
  return Object.fromEntries(keys.map((key) => [key, object[key]])) as Pick<
    T,
    K
  >;
};
